// Archivo: src/pages/dashboard_sections/PequenosDuenos.js (Versión Corregida)

import React, { useState, useEffect } from 'react'; // <-- ESTA ES LA LÍNEA CORREGIDA
import { Shield, Users, BarChart, CheckCircle } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../firebase.js'; // Importamos 'functions'
import StatusBadge from '../../components/common/StatusBadge'; // Importamos el nuevo componente
import { httpsCallable } from 'firebase/functions'; // Importamos httpsCallable
import Swal from 'sweetalert2';
import '../../styles/PequenosDuenos.css';

const PequenosDuenos = ({ user, profileData }) => {
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [request, setRequest] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);

    useEffect(() => {
        // Si el perfil ya indica que es dueño o que la solicitud fue rechazada, no necesitamos buscar más.
        if (profileData?.isSmallOwner || profileData?.smallOwnerStatus === 'rechazado') {
            setRequest({ status: profileData.smallOwnerStatus || 'aprobado' });
            setLoadingStatus(false);
            return;
        }

        // Si el perfil indica que hay una solicitud pendiente, la buscamos.
        if (profileData?.smallOwnerStatus === 'pendiente') {
            const requestRef = doc(db, `users/${user.uid}/small_owner_request/data`);
            const fetchRequest = async () => {
                try {
                    const snap = await getDoc(requestRef);
                    if (snap.exists()) {
                        setRequest(snap.data());
                    }
                } catch (error) {
                    console.error('Error al obtener solicitud de Pequeño Dueño:', error);
                } finally {
                    setLoadingStatus(false);
                }
            };
            fetchRequest();
        } else {
            // Si no hay ningún estado relevante en el perfil, es un usuario nuevo en esta sección.
            setRequest(null);
            setLoadingStatus(false);
        }
    }, [user, profileData]);
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!reason.trim()) {
            Swal.fire('Atención', 'Por favor, cuéntanos por qué te gustaría unirte.', 'info');
            return;
        }
        setLoading(true);

        try {
            // Llamamos a una Cloud Function segura en lugar de escribir directamente en la BD.
            const createSmallOwnerRequest = httpsCallable(functions, 'createSmallOwnerRequest');
            await createSmallOwnerRequest({ reason });
            
            Swal.fire('¡Solicitud Enviada!', 'Un administrador revisará tu solicitud pronto.', 'success');
            setReason('');
        } catch (error) {
            console.error("Error al enviar la solicitud:", error);
            // Los errores de las Cloud Functions vienen con un mensaje más claro para el usuario.
            const errorMessage = error.message || 'No se pudo enviar tu solicitud. Inténtalo de nuevo.';
            Swal.fire('Error', errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelRequest = async () => {
        const result = await Swal.fire({
            title: '¿Estás seguro?', text: "Tu solicitud será eliminada.", icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, cancelar', cancelButtonText: 'No'
        });

        if (result.isConfirmed) {
            setLoading(true);
            try {
                // Llamamos a la nueva Cloud Function segura para cancelar
                const cancelSmallOwnerRequest = httpsCallable(functions, 'cancelSmallOwnerRequest');
                await cancelSmallOwnerRequest();

                Swal.fire('Cancelada', 'Tu solicitud ha sido cancelada.', 'success');
            } catch (err) {
                const errorMessage = err.message || 'No se pudo cancelar la solicitud.';
                Swal.fire('Error', errorMessage, 'error');
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
    };

    const renderContent = () => {
        if (loadingStatus) {
            return <p>Cargando estado de tu solicitud...</p>;
        }

        if (profileData?.isSmallOwner) {
            return (
                <div className="request-status-box approved">
                    <CheckCircle size={40} className="status-icon" />
                    <h3>¡Felicidades, ya eres un Pequeño Dueño!</h3>
                    <p>Ahora tienes acceso a todos los beneficios exclusivos de la membresía.</p>
                </div>
            );
        }

        if (request) {
            return (
                <div className="request-status-box">
                    <h3>Estado de tu Solicitud</h3>
                    <p><strong>Fecha de Envío:</strong> {request.createdAt?.toDate ? request.createdAt.toDate().toLocaleDateString('es-ES') : 'N/A'}</p>
                    <p><strong>Estado:</strong> <StatusBadge status={request.status} /></p>
                    <p><strong>Motivo enviado:</strong> "{request.reason}"</p>
                    
                    {request.status === 'pendiente' && (
                        <button onClick={handleCancelRequest} className="cancel-request-btn" disabled={loading}>
                            {loading ? 'Cancelando...' : 'Cancelar Solicitud'}
                        </button>
                    )}
                </div>
            );
        }

        return (
            <div className="small-owner-form-container">
                <h3 className="section-subtitle">¿Interesado en unirte?</h3>
                <p>Envíanos tu solicitud y un administrador la revisará.</p>
                <form onSubmit={handleSubmit}>
                    <div className="input-group">
                        <label>Cuéntanos por qué te gustaría ser un Pequeño Dueño:</label>
                        <textarea rows="4" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej: Tengo experiencia en liderazgo, quiero hacer crecer mi red, etc." required></textarea>
                    </div>
                    <button type="submit" disabled={loading} className="submit-button-small">
                        {loading ? 'Enviando...' : 'Enviar Solicitud'}
                    </button>
                </form>
            </div>
        );
    };

    return (
        <div className="main-panel">
            <h2 className="section-title">Membresía 'Pequeño Dueño'</h2>
            <p>Accede a beneficios exclusivos y lleva tu liderazgo al siguiente nivel.</p>
            
            <div className="benefits-grid">
                 <div className="benefit-card"><Users className="benefit-icon" size={40} /><h3>Comisiones Extendidas</h3><p>Recibe comisiones de tu red de referidos más allá del tercer nivel.</p></div>
                <div className="benefit-card"><BarChart className="benefit-icon" size={40} /><h3>Reportes Avanzados</h3><p>Obtén acceso a estadísticas detalladas del rendimiento de tu equipo.</p></div>
                <div className="benefit-card"><Shield className="benefit-icon" size={40} /><h3>Soporte Exclusivo</h3><p>Recibe atención personalizada y prioritaria de nuestro equipo de soporte.</p></div>
            </div>

            <div className="form-divider"></div>
            
            {renderContent()}
        </div>
    );
};

export default PequenosDuenos;